import { Upload, ScanSearch, FileOutput, ArrowRight } from "lucide-react";

const FLOW = [
  ["Project Documents", "Specs, addenda, contracts and drawings enter the review."],
  ["Your Company's Scope Rules", "Your playbook — inclusions, exclusions and scope preferences."],
  ["AI Review", "Requirements compared against your scope and the project context."],
  ["Actionable Decisions", "Scope conflicts, addendum impacts, RFIs and clarifications for the estimator and PM."],
];

const STEPS = [
  { n: "01", icon: Upload, title: "Load the bid package", body: "Upload the spec book, contract, addenda and drawings. Mixed PDFs are handled page by page — narrative, scanned pages, sketches and sheets are each read the right way." },
  { n: "02", icon: ScanSearch, title: "Compare against your scope rules", body: "Choose Specification, Contract Risk, AISC/AWS Compliance, Spec-vs-Drawings or Addendum review. Requirements are compared against your company's scope rules and playbook, and every finding cites the document, section and page it came from." },
  { n: "03", icon: FileOutput, title: "Act on what it finds", body: "Findings become a PM Report with Structural Steel and Misc Metals separated, draft RFIs, bid clarifications and an addendum impact summary for the bid team." },
];

function WorkflowStrip() {
  return (
    <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-12" data-testid="workflow-strip">
      {FLOW.map(([title, body], i) => (
        <li key={title} className="relative sc-card px-4 py-3 flex items-start gap-3" data-testid={`workflow-stage-${i + 1}`}>
          <span className="font-mono text-[11px] text-amber-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-100">{title}</div>
            <div className="text-xs text-slate-400 mt-0.5 leading-snug">{body}</div>
          </div>
          {i < FLOW.length - 1 && <ArrowRight className="hidden lg:block absolute -right-[13px] top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500 bg-[#0e131d] rounded-full" aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
}

export function HowItWorks() {
  return (
    <section id="how" className="border-y border-slate-800/60 bg-[#0e131d]" data-testid="how-it-works-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-20">
        <WorkflowStrip />
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="label-cap text-amber-500">// HOW IT WORKS</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100 mt-3">From a 900-page bid set to a list you can act on.</h2>
            <p className="text-sm md:text-base text-slate-400 mt-4 leading-relaxed">Requirements, risks, scope gaps and change impacts — organized the way a fabricator thinks about the job, with the evidence attached.</p>
          </div>
          <ol className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
            {STEPS.map(({ n, icon: Icon, title, body }) => (
              <li key={n} className="sc-card p-6 flex flex-col gap-3" data-testid={`how-step-${n}`}>
                <div className="flex items-center justify-between"><span className="font-mono text-xs text-amber-400">{n}</span><Icon className="w-5 h-5 text-slate-300" /></div>
                <h3 className="font-heading text-lg font-bold text-slate-100">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
